import { Card, Col, Row, Space, Switch, Typography } from "antd";
import { useSelector } from "react-redux";
import {
  getCandidateStatusKey,
  isActiveStatus,
  isFinalStatus,
} from "utilities/candidate";
import { isPermissionPresent } from "utilities/generalUtility";
import { selectUserRoles } from "redux/slices/userSlice";
import K from "utilities/constants";
import { FullSubStatusName } from "../fullSubStatusName/fullSubStatusName";
import { TransitionsSection } from "../transitionsSection/transitionsSection";
import { sortTransitionsBySubStatus } from "../../utils";
import { IncomingTransitionItem } from "../incomingTransitionItem/incomingTransitionItem";
import { OutgoingTransitionItem } from "../outgointTransitionItem/outgoingTransitionItem";
import { useStatusTransitions } from "../../hooks/useStatusTransitions";
import { DisabledSubStatusIcon } from "../../disabledSubStatusIcon/disabledSubStatusIcon";

const { Text } = Typography;

export const SubStatusDetails = ({
  statusesById,
  subStatusesById,
  selectedStatus,
  selectedSubStatus,
  transitions,
  onSubStatusToggle,
  onTransitionToggle,
}) => {
  const userRoles = useSelector(selectUserRoles);

  const { incomingTransitions, outgoingTransitions } = useStatusTransitions(
    transitions,
    selectedSubStatus,
  );

  const sortedIncomingTransitions = sortTransitionsBySubStatus(
    incomingTransitions,
    statusesById,
    subStatusesById,
    {
      statusIdField: "fromStatusId",
      subStatusIdField: "fromSubStatusId",
    },
  );

  const sortedOutgoingTransitions = sortTransitionsBySubStatus(
    outgoingTransitions,
    statusesById,
    subStatusesById,
    {
      statusIdField: "toStatusId",
      subStatusIdField: "toSubStatusId",
    },
  );

  const showSubStatusToggle =
    isActiveStatus(selectedSubStatus.type) &&
    !selectedSubStatus.isInitial &&
    isPermissionPresent(K.Permissions.SuperAdmin, userRoles);

  const showTransitionToggle = (t, toSubStatus) => {
    if (!t.id || !selectedSubStatus.isActive || !toSubStatus.isActive) {
      return false;
    }

    const toSubStatusId = getCandidateStatusKey(t.toStatusId, t.toSubStatusId);

    const otherActiveIncomingCount = transitions.filter(
      (transition) =>
        transition.id !== t.id &&
        transition.isActive &&
        getCandidateStatusKey(
          transition.toStatusId,
          transition.toSubStatusId,
        ) === toSubStatusId,
    ).length;

    return (
      (!t.isActive ||
        isFinalStatus(toSubStatus.type) ||
        otherActiveIncomingCount > 0) &&
      isPermissionPresent(K.Permissions.SuperAdmin, userRoles)
    );
  };

  return (
    <Card bordered={false} bodyStyle={{ padding: 16 }}>
      <Space direction="vertical" style={{ width: "100%" }} size={24}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Space size={12} align="center">
            <FullSubStatusName
              statusName={selectedStatus.name}
              style={{ fontSize: 16 }}
            >
              {selectedSubStatus.name}
            </FullSubStatusName>

            {!selectedSubStatus.isActive && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  color: "rgba(0, 0, 0, 0.5)",
                }}
              >
                <DisabledSubStatusIcon />
                <Text style={{ color: "inherit" }}>Disabled</Text>
              </div>
            )}
          </Space>

          {showSubStatusToggle && (
            <Switch
              checked={selectedSubStatus.isActive}
              onChange={onSubStatusToggle}
            />
          )}
        </div>

        <TransitionsSection
          title="Incoming Transitions"
          description="Candidates can reach this status from:"
        >
          {sortedIncomingTransitions.length === 0 ? (
            <Typography.Text>No incoming transitions.</Typography.Text>
          ) : (
            <Row gutter={[8, 8]}>
              {sortedIncomingTransitions.map((t) => {
                const subStatusId = getCandidateStatusKey(
                  t.fromStatusId,
                  t.fromSubStatusId,
                );

                return (
                  <Col key={subStatusId}>
                    <IncomingTransitionItem
                      isActive={t.isActive}
                      statusName={statusesById[t.fromStatusId].name}
                      subStatusName={subStatusesById[subStatusId].name}
                    />
                  </Col>
                );
              })}
            </Row>
          )}
        </TransitionsSection>

        <TransitionsSection
          title="Outgoing Transitions"
          description="Enable the next candidate status(es):"
        >
          {sortedOutgoingTransitions.length === 0 ? (
            <Typography.Text>No outgoing transitions.</Typography.Text>
          ) : (
            <Space direction="vertical" style={{ width: "100%" }}>
              {sortedOutgoingTransitions.map((t) => {
                const subStatusId = getCandidateStatusKey(
                  t.toStatusId,
                  t.toSubStatusId,
                );

                const toSubStatus = subStatusesById[subStatusId];

                return (
                  <OutgoingTransitionItem
                    key={subStatusId}
                    statusName={statusesById[t.toStatusId].name}
                    subStatusName={subStatusesById[subStatusId].name}
                    isActive={t.isActive}
                    showToggle={showTransitionToggle(t, toSubStatus)}
                    onToggle={(checked) =>
                      onTransitionToggle(t.id, checked, t.isCustom)
                    }
                  />
                );
              })}
            </Space>
          )}
        </TransitionsSection>
      </Space>
    </Card>
  );
};
